'use client';

/**
 * Render surface for V2 address fields. Owns the render loop over the
 * injected `AddressView` (the new `AddressAutocompleteView` at runtime),
 * plus the country-switch `unregister`. Deliberately does not own schema
 * or default-value derivation; those are the concern of
 * `useAddressFieldsData`. See
 * packages/components/docs/design-decisions/0001-address-fields-decomposition.md
 * for the rationale behind this split.
 */

import {
  SupportedCountryCode,
  TAddress,
  stringUtils,
} from '@verifime/utils';
import React, { Fragment, FunctionComponent } from 'react';
import {
  UseFormSetValue,
  UseFormUnregister,
  UseFormWatch,
} from 'react-hook-form';
import { getFieldsNamesFromFieldsAndRulesV1ForAddressOnly } from '../../Form';
import { TAddEntityAddressesV2 } from './useAddEntityV2';
import { COUNTRY_ADDRESS_FIELDS_AND_RULES } from './utils';

/**
 * Contract the V2 renderer uses to render the address capture surface.
 *
 * The concrete implementation lives in `@verifime/views` as
 * `AddressAutocompleteView`. We accept it via dependency injection rather
 * than importing it directly, because `@verifime/views` depends on
 * `@verifime/components` and a direct import would create a cyclic package
 * dependency.
 *
 * The view receives the address as a single `Partial<TAddress>` object plus
 * the active country code. It emits a new `Partial<TAddress>` via `onChange`
 * when the user selects a suggestion or edits the manual-entry inputs. The
 * renderer translates those object-shaped updates into writes on the
 * underlying flat prefixed RHF fields so the backend submission payload
 * shape is preserved (V1 and V2 wrap the same `saveIndividual` /
 * `saveOrganisation` helpers which expect flat `addressSepa*` keys).
 */
export type TAddressViewProps = {
  value: Partial<TAddress>;
  /**
   * Emit the FULL intended state of the address, not a delta. The renderer
   * writes every address sub-field into RHF on each call; keys absent from
   * `value` are treated as cleared (written as `''`). A view that emits a
   * delta will silently wipe untouched fields, including prefilled values.
   */
  onChange: (value: Partial<TAddress>) => void;
  countryCode: SupportedCountryCode;
  label?: string;
  placeholder?: string;
};

export type TAddressView = FunctionComponent<TAddressViewProps>;

const ADDRESS_KEYS = [
  'unitNumber',
  'streetNumber',
  'streetName',
  'streetType',
  'suburb',
  'city',
  'state',
  'postcode',
  'countryCode',
] as const satisfies readonly (keyof TAddress)[];

type FormAddressFieldsProps = {
  addresses: TAddEntityAddressesV2;
  selectedCountryCode: SupportedCountryCode;
  prevSelectedCountryCode: SupportedCountryCode | null;
  setSelectedCountryCode: (code: SupportedCountryCode) => void;
  setValue: UseFormSetValue<any>;
  unregister: UseFormUnregister<any>;
  watch: UseFormWatch<any>;
  onAddressChange: () => void;
  /**
   * Dependency-injected address capture view. Required. Callers in
   * customer-portal typically pass `AddressAutocompleteView` from
   * `@verifime/views`; stories and tests can substitute a stub that
   * conforms to the `TAddressView` contract.
   */
  AddressView: TAddressView;
};

export function FormAddressFields({
  addresses,
  selectedCountryCode,
  prevSelectedCountryCode,
  setSelectedCountryCode,
  setValue,
  unregister,
  watch,
  onAddressChange,
  AddressView,
}: FormAddressFieldsProps) {
  return (
    <>
      {(addresses ?? []).map((address, index) => {
        const prefixedFieldNameFor = (key: keyof TAddress) =>
          stringUtils.generateCamelCaseString(address.fieldsPrefix, String(key));

        const value: Partial<TAddress> = {
          ...(Object.fromEntries(
            ADDRESS_KEYS.map((key) => [key, watch(prefixedFieldNameFor(key))]),
          ) as Partial<TAddress>),
          countryCode: selectedCountryCode,
        };

        const handleAddressChange = (next: Partial<TAddress>) => {
          for (const key of ADDRESS_KEYS) {
            setValue(prefixedFieldNameFor(key), next[key] ?? '', {
              shouldDirty: true,
              shouldValidate: true,
            });
          }
          onAddressChange();
        };

        const addressView = (
          <AddressView
            value={value}
            onChange={handleAddressChange}
            countryCode={selectedCountryCode}
            label={address.inputLabel || address.label}
            placeholder={address.inputPlaceholder}
          />
        );

        if (address.renderType !== 'AddressWithCountrySelect') {
          return <Fragment key={index}>{addressView}</Fragment>;
        }

        // `prevSelectedCountryCode` is hydrated by an effect in `useAddEntityV2`
        // before any user interaction can fire, so the `?? selectedCountryCode`
        // fallback is defensive for the initial-mount window only.
        const handleCountryChange = (newCountryCode: SupportedCountryCode) => {
          const previousCountryCode = prevSelectedCountryCode ?? selectedCountryCode;
          const previousFieldsAndRules =
            COUNTRY_ADDRESS_FIELDS_AND_RULES[previousCountryCode][
              address.fieldsAndRulesName
            ];
          // Surface config drift loudly in development: if a country lands in
          // `AVAILABLE_COUNTRIES` without a matching
          // `COUNTRY_ADDRESS_FIELDS_AND_RULES[country][fieldsAndRulesName]`
          // entry, the lookup returns `undefined` and the unregister step
          // silently no-ops. A console.error makes that gap obvious in dev
          // without introducing a logging library import.
          if (!previousFieldsAndRules) {
            if (process.env.NODE_ENV !== 'production') {
              // eslint-disable-next-line no-console
              console.error(
                `[FormAddressFields] no country rules for ${previousCountryCode}/${address.fieldsAndRulesName}; unregister skipped`,
              );
            }
            setSelectedCountryCode(newCountryCode);
            return;
          }
          const previousFieldsNames =
            getFieldsNamesFromFieldsAndRulesV1ForAddressOnly(previousFieldsAndRules);
          unregister(previousFieldsNames);
          setSelectedCountryCode(newCountryCode);
        };

        return (
          <Fragment key={index}>
            <label>
              Country
              <select
                aria-label="Country"
                value={selectedCountryCode}
                data-field="countryCode"
                onChange={(event) =>
                  handleCountryChange(event.target.value as SupportedCountryCode)
                }
              >
                {Object.keys(COUNTRY_ADDRESS_FIELDS_AND_RULES).map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </label>
            {addressView}
          </Fragment>
        );
      })}
    </>
  );
}

export default FormAddressFields;
